import React, { FC, ReactNode } from "react";

import { DismissRegular } from "@fluentui/react-icons";
import ButterToast, {
  POS_BOTTOM,
  POS_CENTER,
} from "butter-toast/dist/lean.min.js";

type Style = "error" | "info" | "success" | "warning";

export function useToasts() {
  return {
    error: (message: ReactNode) => toast(message, "error"),
    info: (message: ReactNode) => toast(message, "info"),
    success: (message: ReactNode) => toast(message, "success"),
    warning: (message: ReactNode) => toast(message, "warning"),
  };
}

function toast(message: ReactNode, style: Style): void {
  ButterToast.raise({
    content: ({ dismiss }: any) => (
      <Toast onClose={dismiss} style={style}>
        {message}
      </Toast>
    ),
  });
}

export const ToastContainer: FC = () => {
  return (
    <ButterToast
      position={{ horizontal: POS_CENTER, vertical: POS_BOTTOM }}
      spacing={10}
    />
  );
};

const Toast: FC<{ children: ReactNode; style: Style; onClose: () => void; }> = ({
  children,
  onClose,
  style,
}) => {
  const { background, color } = iconStyle(style);

  return (
    <div className="w-100 bg-white rounded-xl p-5 flex items-center justify-between shadow-lg select-none">
      <div className="flex items-center">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${background}`}
        >
          <DismissRegular
            className={`text-gray-500 hover:text-gray-700 ${color}`}
          />
        </div>

        <span className="mx-5 text-gray-700">{children}</span>
      </div>

      <button
        className="cursor-pointer focus:outline-none"
        onClick={onClose}
        title="Close toast"
        type="button"
      >
        <DismissRegular
          className={`text-gray-500 hover:text-gray-700 ${color}`}
        />
      </button>
    </div>
  );
};

function iconStyle(
  theme: Style
): { name: Icons; background: string; color: string } {
  switch (theme) {
    case "error":
      return {
        name: "close",
        background: "bg-red-100",
        color: "text-red-700",
      };

    case "info":
      return {
        name: "visibility",
        background: "bg-blue-400",
        color: "text-white",
      };

    case "success":
      return {
        name: "done",
        background: "bg-teal-100",
        color: "text-teal-700",
      };
    case "warning":
      return {
        name: "error",
        background: "bg-orange-100",
        color: "text-orange-600",
      };
  }
}
